// Caps an open eone-ui dropdown panel at the right edge of the viewport.
//
// index.css gives panels `min-width: max-content` so long options don't wrap. That also overrides
// the max-width eone-ui computes, so a select near the right edge with long options (e.g. a
// source-column list) ran off-screen. CSS alone can't express "viewport minus the panel's inline
// left", so this measures the open panel and, when it overflows, sets --sc-dropdown-panel-cap and
// data-sc-capped (styled in index.css). React doesn't own either, so its re-renders leave them alone.

const EDGE_GAP = 8;
const PANEL_CLASS = 'eone-ui-dropdown-panel';
const CAPPED_ATTR = 'data-sc-capped';
const CAP_VAR = '--sc-dropdown-panel-cap';

function clampPanel(panel: HTMLElement) {
  if (!panel.classList.contains('open')) {
    // Reset on close so the next open (maybe from a different trigger) is measured fresh.
    if (panel.hasAttribute(CAPPED_ATTR)) {
      panel.removeAttribute(CAPPED_ATTR);
      panel.style.removeProperty(CAP_VAR);
    }
    return;
  }

  // Already capped for this open. Returning here also stops our own attribute writes looping.
  if (panel.hasAttribute(CAPPED_ATTR)) return;

  const rect = panel.getBoundingClientRect();
  const limit = window.innerWidth - EDGE_GAP;
  if (rect.width === 0 || rect.right <= limit) return;

  panel.style.setProperty(CAP_VAR, `${Math.max(limit - rect.left, 0)}px`);
  panel.setAttribute(CAPPED_ATTR, '');
}

function watchPanel(panel: HTMLElement) {
  // style/class cover open/close and repositioning; childList covers options loading in after open.
  new MutationObserver(() => clampPanel(panel)).observe(panel, {
    attributes: true,
    attributeFilter: ['style', 'class'],
    childList: true,
    subtree: true,
  });
  clampPanel(panel);
}

function install() {
  if (typeof document === 'undefined' || !document.body) return;

  // Panels are portaled straight into <body>, so direct children are enough.
  document.body
    .querySelectorAll<HTMLElement>(`:scope > .${PANEL_CLASS}`)
    .forEach(watchPanel);

  new MutationObserver((records) => {
    for (const record of records) {
      record.addedNodes.forEach((node) => {
        if (node instanceof HTMLElement && node.classList.contains(PANEL_CLASS)) watchPanel(node);
      });
    }
  }).observe(document.body, { childList: true });
}

if (typeof document !== 'undefined') {
  if (document.body) install();
  else document.addEventListener('DOMContentLoaded', install, { once: true });
}
